import type { Feeding, PumpingSession, SleepSession } from '@baby-tracker/contracts';
import type { VolumeDisplayUnit } from '@baby-tracker/validation';
import { volumeToMillilitres } from '@baby-tracker/validation';
import { useState, type ReactElement } from 'react';

import { useNow } from '../../../hooks/useNow.js';
import {
    useStopFeeding,
    useStopPumping,
    useStopSleep,
    useSwitchFeedingSide,
} from '../../../hooks/useTracking.js';
import { elapsedSeconds, formatDuration } from '../../../lib/activity-format.js';
import { sleepKindLabels } from '../../../lib/labels.js';
import { Button } from '../../ui/Button.js';
import { errorText } from '../../ui/error-text.js';
import { FormError } from '../../ui/FormError.js';
import fieldStyles from '../../ui/fields.module.css';
import { TextField } from '../../ui/TextField.js';
import styles from './tracking.module.css';

export function TimerBanner({
    familyId,
    volumeUnit,
    openFeeding,
    openSleep,
    openPump,
}: {
    familyId: string;
    volumeUnit: VolumeDisplayUnit;
    openFeeding: Feeding | null;
    openSleep: SleepSession | null;
    openPump: PumpingSession | null;
}): ReactElement | null {
    const running = openFeeding !== null || openSleep !== null || openPump !== null;
    const now = useNow(running);
    const stopFeeding = useStopFeeding(familyId);
    const switchSide = useSwitchFeedingSide(familyId);
    const stopSleep = useStopSleep(familyId);
    const stopPump = useStopPumping(familyId);
    const [pumpTotal, setPumpTotal] = useState('');
    const [error, setError] = useState<string | null>(null);

    if (!running) {
        return null;
    }

    async function run(task: () => Promise<unknown>): Promise<void> {
        setError(null);
        try {
            await task();
        } catch (caught) {
            setError(errorText(caught));
        }
    }

    const breastElapsed =
        openFeeding === null
            ? 0
            : (openFeeding.leftDurationS ?? 0) +
              (openFeeding.rightDurationS ?? 0) +
              (openFeeding.activeSideStartedAt === null
                  ? 0
                  : elapsedSeconds(openFeeding.activeSideStartedAt, now));

    return (
        <div className={styles.banner} role="status">
            {openFeeding ? (
                <div className={styles.bannerRow}>
                    <p className={styles.timer}>
                        Breast ·{' '}
                        {openFeeding.activeSide === 'left'
                            ? 'Left'
                            : openFeeding.activeSide === 'right'
                              ? 'Right'
                              : 'Running'}{' '}
                        · {formatDuration(breastElapsed)}
                    </p>
                    <div className={fieldStyles.row}>
                        <Button
                            variant="secondary"
                            loading={switchSide.isPending}
                            onClick={() => {
                                const side = openFeeding.activeSide === 'right' ? 'left' : 'right';
                                void run(() =>
                                    switchSide.mutateAsync({ id: openFeeding.id, body: { side } }),
                                );
                            }}
                        >
                            {openFeeding.activeSide === 'right'
                                ? 'Switch to left'
                                : 'Switch to right'}
                        </Button>
                        <Button
                            loading={stopFeeding.isPending}
                            onClick={() => {
                                void run(() =>
                                    stopFeeding.mutateAsync({ id: openFeeding.id, body: {} }),
                                );
                            }}
                        >
                            Stop breast
                        </Button>
                    </div>
                </div>
            ) : null}
            {openSleep ? (
                <div className={styles.bannerRow}>
                    <p className={styles.timer}>
                        {sleepKindLabels[openSleep.kind]} ·{' '}
                        {formatDuration(elapsedSeconds(openSleep.startAt, now))}
                    </p>
                    <Button
                        loading={stopSleep.isPending}
                        onClick={() => {
                            void run(() => stopSleep.mutateAsync({ id: openSleep.id, body: {} }));
                        }}
                    >
                        Stop sleep
                    </Button>
                </div>
            ) : null}
            {openPump ? (
                <div className={styles.bannerRow}>
                    <p className={styles.timer}>
                        Pump · {formatDuration(elapsedSeconds(openPump.startAt, now))}
                    </p>
                    <form
                        className={fieldStyles.row}
                        onSubmit={(event) => {
                            event.preventDefault();
                            const trimmed = pumpTotal.trim();
                            const parsed = Number(trimmed);
                            if (trimmed.length > 0 && (!Number.isFinite(parsed) || parsed <= 0)) {
                                setError('Enter a number greater than 0');
                                return;
                            }
                            void run(async () => {
                                await stopPump.mutateAsync({
                                    id: openPump.id,
                                    body:
                                        trimmed.length > 0
                                            ? { totalMl: volumeToMillilitres(parsed, volumeUnit) }
                                            : {},
                                });
                                setPumpTotal('');
                            });
                        }}
                    >
                        <TextField
                            label={`Total (${volumeUnit}, optional)`}
                            inputMode="decimal"
                            value={pumpTotal}
                            onChange={(event) => {
                                setPumpTotal(event.target.value);
                            }}
                        />
                        <Button type="submit" loading={stopPump.isPending}>
                            Stop pump
                        </Button>
                    </form>
                </div>
            ) : null}
            <FormError message={error} />
        </div>
    );
}
